(() => {
  'use strict';

  if (window.SignGuyMotion) return;

  const rootElement = document.documentElement;
  // Keep motion default-on when an older theme settings payload has not stored the new keys yet.
  if (!rootElement.dataset.sgAnimations) rootElement.dataset.sgAnimations = 'true';
  if (!rootElement.dataset.sgAnimationIntensity) rootElement.dataset.sgAnimationIntensity = 'standard';
  if (!rootElement.dataset.sgHeroDepth) rootElement.dataset.sgHeroDepth = 'true';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopDepth = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const revealSelector = '[data-sg-reveal], .scroll-trigger.animate--fade-in, .scroll-trigger.animate--slide-in, [data-sg-count]';
  const legacySelector = '.scroll-trigger.animate--fade-in, .scroll-trigger.animate--slide-in';
  const initialized = new WeakSet();
  const countStates = new WeakMap();
  const depthStates = new WeakMap();
  const headerStates = new WeakMap();
  const heroStates = new WeakMap();
  const sectionMotionStates = new WeakMap();
  const utilityObserverStates = new WeakMap();
  const cartStates = new WeakMap();
  const productStates = new WeakMap();
  const productMediaStates = new WeakMap();
  const recentListStates = new WeakMap();
  const collectionHeaderStates = new WeakMap();
  const resultGridStates = new WeakMap();
  const resultsContainerStates = new WeakMap();
  const galleryFilterStates = new WeakMap();
  const filterChipKnown = new WeakSet();
  const secondaryMotionStates = new WeakMap();
  const cartPageStates = new WeakMap();
  const cartFooterStates = new WeakMap();
  const formStates = new WeakMap();
  const formStatusSeen = new WeakSet();
  const productButtonLabels = new WeakMap();
  const pendingProductRoots = new Set();
  let observer = null;
  let depthObserver = null;
  let heroTrustObserver = null;
  let sectionObserver = null;
  let sectionGlowObserver = null;
  let secondaryObserver = null;
  let lateSectionObserver = null;
  let lastConfirmedAdd = Number.NEGATIVE_INFINITY;
  let productCartSubscription = null;
  let productCartErrorSubscription = null;
  let cartPageKnownKeys = new Set();
  const cartPageSnapshots = new Map();
  let cartPageSubtotalText = '';
  const initialNavigationWasBackForward = window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';

  const animationsEnabled = () => rootElement.dataset.sgAnimations === 'true';
  const depthEnabled = () => rootElement.dataset.sgHeroDepth === 'true';
  const canAnimate = () => animationsEnabled() && !reducedMotion.matches;
  const cssMilliseconds = (name, fallback) => {
    const value = window.getComputedStyle(rootElement).getPropertyValue(name).trim();
    if (!value) return fallback;
    if (value.endsWith('ms')) return Number.parseFloat(value);
    if (value.endsWith('s')) return Number.parseFloat(value) * 1000;
    return fallback;
  };

  const elementsIn = (scope, selector) => {
    const elements = [];
    if (scope instanceof Element && scope.matches(selector)) elements.push(scope);
    if (scope.querySelectorAll) elements.push(...scope.querySelectorAll(selector));
    return elements;
  };

  const countStateFor = (element) => {
    if (countStates.has(element)) return countStates.get(element);

    const finalText = element.textContent;
    const explicitTarget = element.getAttribute('data-sg-count');
    const visibleMatch = finalText.match(/-?[\d,.]+/);
    const targetSource = explicitTarget && explicitTarget.trim() ? explicitTarget : visibleMatch?.[0];
    const targetMatch = targetSource?.match(/-?[\d,.]+/);
    if (!targetMatch) return null;

    const normalizedTarget = targetMatch[0].replace(/,/g, '');
    const target = Number(normalizedTarget);
    if (!Number.isFinite(target)) return null;

    const decimalPoint = normalizedTarget.lastIndexOf('.');
    const decimals = decimalPoint === -1 ? 0 : normalizedTarget.length - decimalPoint - 1;
    const prefix = visibleMatch ? finalText.slice(0, visibleMatch.index) : '';
    const suffix = visibleMatch ? finalText.slice(visibleMatch.index + visibleMatch[0].length) : '';
    const formatter = new Intl.NumberFormat(rootElement.lang || undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: targetMatch[0].includes(','),
    });
    const state = { decimals, finalText, formatter, prefix, suffix, target, frame: 0 };
    countStates.set(element, state);
    return state;
  };

  const finishCount = (element) => {
    const state = countStateFor(element);
    if (!state) return;
    if (state.frame) cancelAnimationFrame(state.frame);
    state.frame = 0;
    element.textContent = state.finalText;
  };

  const animateCount = (element) => {
    const state = countStateFor(element);
    if (!state || reducedMotion.matches || !animationsEnabled()) {
      finishCount(element);
      return;
    }

    const duration = cssMilliseconds('--sg-motion-hero', 800);
    const startedAt = performance.now();
    const render = (time) => {
      const progress = Math.min((time - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = state.target * eased;
      element.textContent = `${state.prefix}${state.formatter.format(value)}${state.suffix}`;
      if (progress < 1) {
        state.frame = requestAnimationFrame(render);
      } else {
        finishCount(element);
      }
    };
    state.frame = requestAnimationFrame(render);
  };

  const reveal = (element) => {
    element.classList.remove('sg-motion-pending', 'scroll-trigger--offscreen');
    element.classList.add('sg-motion-revealed');
    if (element.hasAttribute('data-sg-reveal')) {
      element.classList.add('sg-motion-animating');
      const delay = Number(element.style.getPropertyValue('--sg-stagger-index') || 0) * cssMilliseconds('--sg-stagger-step', 65);
      window.setTimeout(() => element.classList.remove('sg-motion-animating'), cssMilliseconds('--sg-motion-reveal', 500) + delay + 50);
    }
    if (element.hasAttribute('data-sg-count')) animateCount(element);
  };

  const getObserver = () => {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    return observer;
  };

  const prepareStaggers = (scope) => {
    elementsIn(scope, '[data-sg-stagger]').forEach((group) => {
      group.querySelectorAll('[data-sg-reveal]').forEach((element, index) => {
        element.style.setProperty('--sg-stagger-index', String(index));
      });
    });
  };

  const prepareReveal = (element) => {
    if (initialized.has(element) || element.closest('[data-sg-section], [data-sg-product]')) return;
    initialized.add(element);

    if (element.hasAttribute('data-sg-reveal')) element.classList.add('sg-motion-pending');
    if (element.matches(legacySelector)) element.classList.add('scroll-trigger--offscreen');
    if (element.hasAttribute('data-sg-count')) {
      const state = countStateFor(element);
      if (state) element.textContent = `${state.prefix}${state.formatter.format(0)}${state.suffix}`;
    }
    getObserver().observe(element);
  };

  const scheduleDepth = (container, state) => {
    if (state.frame || !state.visible) return;
    state.frame = requestAnimationFrame(() => {
      state.frame = 0;
      if (!state.visible) return;

      state.currentX += (state.targetX - state.currentX) * .14;
      state.currentY += (state.targetY - state.currentY) * .14;
      const settled = Math.abs(state.targetX - state.currentX) < .01 && Math.abs(state.targetY - state.currentY) < .01;
      if (settled) {
        state.currentX = state.targetX;
        state.currentY = state.targetY;
      }

      container.querySelectorAll('[data-sg-hero-depth-layer]').forEach((layer, index) => {
        const amount = Math.max(-6, Math.min(6, Number(layer.dataset.sgHeroDepthLayer) || (index + 1) * 1.5));
        layer.style.transform = `translate3d(${state.currentX * amount}px, ${state.currentY * amount}px, 0)`;
      });

      if (!settled) {
        scheduleDepth(container, state);
      } else if (state.targetX === 0 && state.targetY === 0) {
        container.classList.remove('sg-depth-active', 'sg-depth-resetting');
        container.querySelectorAll('[data-sg-hero-depth-layer]').forEach((layer) => {
          layer.style.transform = '';
        });
      }
    });
  };

  const resetDepth = (scope = document, immediate = true) => {
    elementsIn(scope, '[data-sg-hero-depth]').forEach((container) => {
      const state = depthStates.get(container);
      if (!state) return;
      state.targetX = 0;
      state.targetY = 0;
      container.classList.add('sg-depth-resetting');
      if (!immediate && state.visible) {
        scheduleDepth(container, state);
        return;
      }
      if (state.frame) cancelAnimationFrame(state.frame);
      state.frame = 0;
      state.currentX = 0;
      state.currentY = 0;
      container.classList.remove('sg-depth-active', 'sg-depth-resetting');
      container.querySelectorAll('[data-sg-hero-depth-layer]').forEach((layer) => layer.style.transform = '');
    });
  };

  const getDepthObserver = () => {
    if (depthObserver || !('IntersectionObserver' in window)) return depthObserver;
    depthObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const state = depthStates.get(entry.target);
        if (!state) return;
        state.visible = entry.isIntersecting;
        if (!state.visible) resetDepth(entry.target, true);
      });
    }, { threshold: 0 });
    return depthObserver;
  };

  const prepareDepth = (container) => {
    if (depthStates.has(container) || !canAnimate() || !depthEnabled() || !desktopDepth.matches || !('IntersectionObserver' in window)) return;

    const state = { currentX: 0, currentY: 0, frame: 0, targetX: 0, targetY: 0, visible: true };
    const move = (event) => {
      if (!state.visible || !canAnimate() || !depthEnabled() || !desktopDepth.matches || container.classList.contains('sg-hero-entrance-ready')) return;
      const bounds = container.getBoundingClientRect();
      const normalizedX = ((event.clientX - bounds.left) / bounds.width - .5) * 2;
      const normalizedY = ((event.clientY - bounds.top) / bounds.height - .5) * 2;
      state.targetX = Math.max(-1, Math.min(1, normalizedX));
      state.targetY = Math.max(-1, Math.min(1, normalizedY));
      container.classList.add('sg-depth-active');
      scheduleDepth(container, state);
    };
    const leave = () => resetDepth(container, false);
    container.addEventListener('pointermove', move, { passive: true });
    container.addEventListener('pointerleave', leave, { passive: true });
    state.leave = leave;
    state.move = move;
    depthStates.set(container, state);
    getDepthObserver().observe(container);
  };

  const prepareDepthIn = (scope) => {
    elementsIn(scope, '[data-sg-hero-depth]').forEach(prepareDepth);
  };

  const setHeroPart = (hero, selector, part) => {
    const element = hero.querySelector(selector);
    if (element) element.dataset.sgHeroPart = part;
    return element;
  };

  const prepareHeaderEntrance = (scope = document) => {
    elementsIn(scope, '.header-wrapper .header').forEach((header) => {
      if (headerStates.has(header)) return;

      header.dataset.sgHeaderEntrance = '';
      header.querySelectorAll('.sg-desktop-nav .list-menu--inline > li').forEach((item, index) => {
        item.dataset.sgHeaderNavItem = '';
        item.style.setProperty('--sg-header-nav-index', String(index));
      });

      const state = { timer: 0 };
      headerStates.set(header, state);
      if (!canAnimate() || window.Shopify?.designMode || initialNavigationWasBackForward) {
        header.classList.add('sg-header-entered');
        return;
      }

      // The opt-in class is added only after the live header has been mapped, preserving no-JS visibility.
      header.classList.add('sg-header-entrance-ready');
      state.timer = window.setTimeout(() => {
        header.classList.remove('sg-header-entrance-ready');
        header.classList.add('sg-header-entered');
        state.timer = 0;
      }, 880);
    });
  };

  const getHeroTrustObserver = () => {
    if (heroTrustObserver || !('IntersectionObserver' in window)) return heroTrustObserver;
    heroTrustObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const hero = entry.target.closest('[data-sg-hero]');
        const state = heroStates.get(hero);
        heroTrustObserver.unobserve(entry.target);
        if (!hero || !state || state.trustGlowPlayed || state.trustGlowQueued || !canAnimate()) return;
        state.trustGlowQueued = true;
        const start = () => {
          state.trustGlowTimer = 0;
          state.trustGlowQueued = false;
          if (!canAnimate()) return;
          state.trustGlowPlayed = true;
          hero.classList.add('sg-hero-trust-glow-sequence');
        };
        const delay = Math.max(0, state.entranceEndsAt - performance.now() + 80);
        if (delay) {
          state.trustGlowTimer = window.setTimeout(start, delay);
        } else {
          start();
        }
      });
    }, { threshold: 0.65 });
    return heroTrustObserver;
  };

  const observeHeroTrustGlow = (hero, trust) => {
    const state = heroStates.get(hero);
    if (!state || !trust || state.trustGlowPlayed || state.trustGlowQueued || !canAnimate() || !('IntersectionObserver' in window)) return;
    getHeroTrustObserver().observe(trust);
  };

  const prepareHero = (hero) => {
    if (heroStates.has(hero)) return;

    hero.dataset.sgHero = '';
    hero.dataset.sgHeroDepth = '';
    const artwork = hero.querySelector('.hero-slideshow-image');
    if (artwork) {
      artwork.dataset.sgHeroLayer = 'background-composite';
      artwork.dataset.sgHeroCompositeParts = 'main-sign side-signs hype-chain';
      artwork.dataset.sgHeroDepthLayer = '2';
    }

    setHeroPart(hero, '.sg-hero-eyebrow', 'eyebrow');
    setHeroPart(hero, '.sg-hero-heading', 'heading');
    setHeroPart(hero, '.sg-hero-offer', 'supporting-heading');
    const highlight = hero.querySelector('.sg-hero-highlight');
    if (highlight) highlight.dataset.sgHeroGlowText = highlight.textContent.trim();
    setHeroPart(hero, '.sg-hero-text', 'description');
    const buttons = setHeroPart(hero, '.sg-hero-buttons', 'ctas');
    buttons?.querySelector('.sg-hero-button-primary')?.setAttribute('data-sg-hero-cta', 'primary');
    buttons?.querySelector('.sg-hero-button-secondary')?.setAttribute('data-sg-hero-cta', 'secondary');
    const trust = setHeroPart(hero, '.sg-hero-trustbar', 'trust-statistics');
    trust?.querySelectorAll('.sg-hero-trust-item').forEach((item, index) => {
      item.dataset.sgHeroPart = 'trust-stat';
      item.style.setProperty('--sg-hero-trust-index', String(index));
    });

    let glow = hero.querySelector('[data-sg-hero-part="main-sign-glow"]');
    if (!glow) {
      glow = document.createElement('span');
      glow.className = 'sg-hero-glow';
      glow.dataset.sgHeroPart = 'main-sign-glow';
      glow.setAttribute('aria-hidden', 'true');
      hero.appendChild(glow);
    }

    const state = {
      entranceEndsAt: 0,
      timer: 0,
      trust,
      trustGlowPlayed: false,
      trustGlowQueued: false,
      trustGlowTimer: 0,
    };
    heroStates.set(hero, state);

    // The hidden entrance state exists only after every required hero role has been mapped.
    if (!canAnimate() || window.Shopify?.designMode) {
      hero.classList.add('sg-hero-entered');
      return;
    }
    hero.classList.add('sg-hero-entrance-ready');
    state.entranceEndsAt = performance.now() + 1650;
    observeHeroTrustGlow(hero, trust);
    state.timer = window.setTimeout(() => {
      hero.classList.remove('sg-hero-entrance-ready');
      hero.classList.add('sg-hero-entered');
      state.entranceEndsAt = 0;
      state.timer = 0;
    }, 1650);
  };

  const prepareHeroes = (scope) => {
    elementsIn(scope, '.home-slideshow-section .sg-hero-slide').forEach(prepareHero);
  };

  const setSectionPart = (section, selector, part, all = false) => {
    const elements = all ? [...section.querySelectorAll(selector)] : [section.querySelector(selector)].filter(Boolean);
    elements.forEach((element) => element.dataset.sgSectionPart = part);
    return elements;
  };

  const setSectionGroup = (section, selector, group) => {
    const element = section.querySelector(selector);
    if (element) element.dataset.sgSectionGroup = group;
    return element;
  };

  const setSectionItems = (elements, step = 60, mode = 'sequence', base = 0) => {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const rowTops = [];
    elements.forEach((element, index) => {
      let itemIndex = mode === 'together' ? 0 : index;
      if (mobile && mode === 'rows') {
        const top = Math.round(element.offsetTop / 4) * 4;
        if (!rowTops.includes(top)) rowTops.push(top);
        itemIndex = rowTops.indexOf(top);
      }
      element.dataset.sgSectionItem = '';
      element.style.setProperty('--sg-section-delay', `${Math.min(base + itemIndex * step, 400)}ms`);
    });
  };

  const mapStudioDesign = (section) => {
    setSectionPart(section, '.sg-home-studio__header', 'copy');
    const heading = section.querySelector('.sg-home-studio h2');
    if (heading) heading.dataset.sgSectionAccent = '';
    setSectionGroup(section, '.sg-home-studio__cards', 'cards');
    const cards = [...section.querySelectorAll('.sg-home-studio__card')];
    cards.forEach((card, index) => card.style.setProperty('--sg-studio-card-index', String(index)));
    setSectionItems(cards, 65, 'rows', 100);
  };

  const mapProductCategories = (section) => {
    setSectionPart(section, '.sg-product-cards__heading-wrap', 'heading');
    setSectionGroup(section, '.sg-product-cards__grid', 'cards');
    const cards = [...section.querySelectorAll('.sg-product-cards__card')];
    cards.forEach((card, index) => card.style.setProperty('--sg-product-card-glow-index', String(index)));
    setSectionItems(cards, 60, 'sequence', 90);
  };

  const mapStudioWorkflow = (section) => {
    setSectionPart(section, '.sg-studio-feature__media', 'mockup');
    setSectionPart(section, '.sg-studio-feature__content', 'copy');
    setSectionGroup(section, '.sg-studio-feature__steps', 'connector');
    setSectionItems([...section.querySelectorAll('.sg-studio-feature__step')], 85, 'sequence', 140);
  };

  const mapBestSellers = (section) => {
    setSectionPart(section, '.sg-product-carousel__heading-wrap', 'heading');
    section.querySelectorAll('.sg-product-carousel__line').forEach((line) => line.dataset.sgSectionAccent = '');
    setSectionPart(section, '.sg-product-carousel__button-wrap', 'cta');
    const scroller = section.querySelector('.sg-product-carousel__scroller');
    if (!scroller) return;
    const scrollerBounds = scroller.getBoundingClientRect();
    const visibleCards = [];
    section.querySelectorAll('.sg-product-carousel__card').forEach((card) => {
      const bounds = card.getBoundingClientRect();
      const visible = bounds.right > scrollerBounds.left + 1 && bounds.left < scrollerBounds.right - 1;
      if (visible) {
        visibleCards.push(card);
      } else {
        card.dataset.sgSectionStatic = '';
      }
    });
    setSectionItems(visibleCards, 55, 'sequence', 90);
  };

  const mapLeagues = (section) => {
    setSectionPart(section, '.sg-leagues__heading-wrap', 'heading');
    setSectionGroup(section, '.sg-leagues__grid', 'tiles');
    setSectionItems([...section.querySelectorAll('.sg-leagues__card')], 45, 'rows', 70);
  };

  const mapPerfectFor = (section) => {
    setSectionPart(section, '.sg-perfect-for__heading-wrap', 'heading');
    setSectionGroup(section, '.sg-perfect-for__grid', 'cards');
    setSectionItems([...section.querySelectorAll('.sg-perfect-for__card')], 0, 'together', 100);
  };

  const mapPromotion = (section) => {
    const artwork = section.querySelector('.single-banner-wrapper');
    if (artwork) {
      artwork.classList.remove('scroll-trigger--offscreen');
      artwork.dataset.sgPromotionComposite = 'left-product right-product';
      let background = artwork.querySelector('[data-sg-section-part="promotion-background"]');
      if (!background) {
        background = document.createElement('span');
        background.className = 'sg-promotion-background';
        background.dataset.sgSectionPart = 'promotion-background';
        background.setAttribute('aria-hidden', 'true');
        background.style.backgroundImage = window.getComputedStyle(artwork).backgroundImage;
        artwork.prepend(background);
        artwork.style.backgroundImage = 'none';
      }
    }
    setSectionPart(section, '.single-banner-text', 'offer-copy');
    setSectionPart(section, '.single-banner_btn_t', 'promotion-cta');
  };

  const mapGallery = (section) => {
    setSectionPart(section, '.sg-gallery-preview__header', 'copy');
    setSectionGroup(section, '.sg-gallery-preview__grid', 'collage');
    const cards = [...section.querySelectorAll('.sg-gallery-preview__card')];
    setSectionItems(cards, 55, 'sequence', 90);
    cards.forEach((card, index) => card.style.setProperty('--sg-gallery-tilt', `${index % 2 ? 1 : -1}deg`));
  };

  const mapBuiltByFans = (section) => {
    // Retire the section-local reveal classes so the global lifecycle is the sole owner.
    section.classList.remove('sg-built-by-fans--motion-ready', 'sg-built-by-fans--is-visible');
    section.dataset.sgBuiltByFansReady = 'true';
    setSectionPart(section, '.sg-built-by-fans__media', 'founder-image');
    setSectionPart(section, '.sg-built-by-fans__content', 'story-copy');
    setSectionPart(section, '.sg-built-by-fans__signature', 'signature');
    setSectionPart(section, '.sg-built-by-fans__actions', 'cta');
    setSectionItems([...section.querySelectorAll('.sg-built-by-fans__card')], 65, 'rows', 150);
  };

  const mapReviews = (section) => {
    setSectionPart(section, '.jdgm-header', 'review-header');
    const stars = setSectionPart(section, '.jdgm-header .jdgm-star', 'review-star', true);
    stars.forEach((star, index) => star.style.setProperty('--sg-section-delay', `${Math.min(index * 40, 160)}ms`));
  };

  const mapFooter = (section) => {
    const benefits = [...section.querySelectorAll('.sg-footer-benefit')];
    benefits.forEach((benefit) => benefit.dataset.sgSectionPart = 'trust-badge');
    setSectionItems(benefits, 55, 'rows');

    const groups = [
      section.querySelector('.sg-footer-brand-area'),
      ...section.querySelectorAll('.sg-footer-column'),
      section.querySelector('.sg-footer-newsletter'),
    ].filter(Boolean);
    groups.forEach((group) => group.dataset.sgSectionPart = 'footer-group');
    setSectionItems(groups, 50, 'rows', 140);
  };

  const mapProductUpsells = (section) => {
    const heading = section.querySelector('[data-sg-product-upsells-part]');
    if (heading) heading.style.setProperty('--sg-product-delay', '0ms');
    section.querySelectorAll('[data-sg-product-upsell]').forEach((item, index) => {
      item.style.setProperty('--sg-product-delay', `${Math.min(70 + index * 70, 280)}ms`);
    });
  };

  const mapProductBuild = (section) => {
    section.querySelector('[data-sg-product-build-part="heading"]')?.style.setProperty('--sg-product-delay', '0ms');
    section.querySelector('[data-sg-product-build-part="steps"]')?.style.setProperty('--sg-product-delay', '50ms');
    section.querySelectorAll('[data-sg-product-build-step]').forEach((step, index) => {
      step.style.setProperty('--sg-product-delay', `${Math.min(70 + index * 70, 280)}ms`);
    });
    section.querySelector('.sg-product-specs-panel')?.style.setProperty('--sg-product-delay', '310ms');
    section.querySelector('[data-sg-product-final-cta]')?.style.setProperty('--sg-product-delay', '380ms');
  };

  const sectionDefinitions = [
    ['.sg-home-studio', 'studio-design', mapStudioDesign],
    ['.sg-product-cards', 'product-categories', mapProductCategories],
    ['.sg-studio-feature', 'studio-workflow', mapStudioWorkflow],
    ['.sg-product-carousel', 'best-sellers', mapBestSellers],
    ['.sg-leagues', 'leagues', mapLeagues],
    ['.sg-perfect-for', 'perfect-for', mapPerfectFor],
    ['.single-banner-section', 'promotion', mapPromotion],
    ['.sg-gallery-preview', 'gallery', mapGallery],
    ['.sg-built-by-fans', 'built-by-fans', mapBuiltByFans],
    ['.jdgm-cards-carousel', 'reviews', mapReviews],
    ['.sg-dark-footer', 'footer', mapFooter],
    ['[data-sg-product-upsells]', 'product-upsells', mapProductUpsells],
    ['[data-sg-product-build]', 'product-build', mapProductBuild],
  ];

  const getSectionObserver = () => {
    if (sectionObserver || !('IntersectionObserver' in window)) return sectionObserver;
    sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sg-section-visible');
        sectionObserver.unobserve(entry.target);
        const state = sectionMotionStates.get(entry.target);
        if (state) {
          state.timer = window.setTimeout(() => {
            entry.target.classList.remove('sg-section-motion-ready');
            entry.target.classList.add('sg-section-entered');
            state.timer = 0;
          }, 900);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });
    return sectionObserver;
  };

  const getSectionGlowObserver = () => {
    if (sectionGlowObserver || !('IntersectionObserver' in window)) return sectionGlowObserver;
    sectionGlowObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < .999) return;
        const section = entry.target.closest('[data-sg-section]');
        if (!section) return;
        const state = sectionMotionStates.get(section);
        if (!state) return;
        section.classList.add('sg-section-glow-visible');
        state.glowPlayed = true;
        sectionGlowObserver.unobserve(entry.target);
      });
    }, { threshold: [0, .999] });
    return sectionGlowObserver;
  };

  const prepareSectionGlow = (section) => {
    const state = sectionMotionStates.get(section);
    if (!state || state.glowPlayed || state.glowObserved) return;
    const group = section.querySelector('[data-sg-section-group="cards"]');
    if (!group) return;
    let target = group.querySelector(':scope > [data-sg-glow-trigger]');
    if (!target) {
      target = document.createElement('span');
      target.dataset.sgGlowTrigger = '';
      target.setAttribute('aria-hidden', 'true');
      group.appendChild(target);
    }
    state.glowTarget = target;

    if (!canAnimate() || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      state.glowPlayed = true;
      section.classList.add('sg-section-glow-visible');
      return;
    }

    state.glowObserved = true;
    getSectionGlowObserver().observe(target);
  };

  const prepareSection = (section, name, mapper) => {
    if (sectionMotionStates.has(section)) {
      if (name === 'reviews') mapper(section);
      return;
    }
    section.dataset.sgSection = name;
    mapper(section);
    const state = {};
    sectionMotionStates.set(section, state);

    if (!canAnimate() || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      section.classList.add('sg-section-visible');
      return;
    }
    section.classList.add('sg-section-motion-ready');
    getSectionObserver().observe(section);
  };

  const prepareSections = (scope = document) => {
    sectionDefinitions.forEach(([selector, name, mapper]) => {
      elementsIn(scope, selector).forEach((section) => prepareSection(section, name, mapper));
    });
    elementsIn(scope, '[data-sg-section="studio-design"], [data-sg-section="product-categories"]').forEach(prepareSectionGlow);

    // Future first-party homepage sections receive one coordinated group entrance until given a bespoke map.
    if (!document.querySelector('.home-slideshow-section')) return;
    elementsIn(scope, '#MainContent > .shopify-section').forEach((wrapper) => {
      if (wrapper.matches('[data-sg-section]') || wrapper.querySelector('[data-sg-section], [data-sg-hero]')) return;
      if (wrapper.querySelector('iframe, .shopify-app-block, .shopify-block, [data-shopify-editor-block*="@app"]')) return;
      const section = wrapper.querySelector(':scope > section, :scope > div') || wrapper;
      prepareSection(section, 'content-section', (contentSection) => {
        const groups = [...contentSection.children].filter((element) => (
          !element.matches('script, style, noscript, template') &&
          !element.matches('[hidden]')
        ));
        if (!groups.length) {
          contentSection.dataset.sgSectionPart = 'content-group';
          return;
        }
        groups.forEach((group) => group.dataset.sgSectionPart = 'content-group');
        setSectionItems(groups, 55, 'rows');
      });
    });
  };

  const watchLateSections = () => {
    if (lateSectionObserver || !document.body) return;
    lateSectionObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          prepareSections(node);
          prepareCollectionMotion(node, false);
          prepareSecondaryMotion(node, false);
          const reviewRoot = node.closest('[data-sg-section="reviews"]');
          if (reviewRoot) mapReviews(reviewRoot);
        });
      });
    });
    lateSectionObserver.observe(document.body, { childList: true, subtree: true });
  };

  const prepareMenuMotion = (scope) => {
    elementsIn(scope, '.sg-desktop-nav details').forEach((details) => {
      let groups = details.querySelectorAll('.mega-menu__list > li');
      if (!groups.length) groups = details.querySelectorAll(':scope > .header__submenu > li');
      groups.forEach((group, index) => {
        group.style.setProperty('--sg-menu-index', String(index));
        group.style.setProperty('--sg-menu-reverse-index', String(Math.min(groups.length - index - 1, 3)));
      });
    });

    elementsIn(scope, 'header-drawer .menu-drawer, header-drawer .menu-drawer__submenu').forEach((panel) => {
      panel.querySelectorAll('.menu-drawer__menu-item, .sg-menu-quick-links a').forEach((item, index) => {
        item.style.setProperty('--sg-menu-stagger', `${Math.min(index * 22, 250)}ms`);
      });
    });
  };

  const preparePredictiveResults = (scope) => {
    const listItems = elementsIn(scope, '.predictive-search__list-item');
    const items = listItems.length ? listItems : elementsIn(scope, '.predictive-search__item');
    items.forEach((item, index) => {
      if (item.dataset.sgPredictiveReady === 'true') return;
      item.dataset.sgPredictiveReady = 'true';
      item.style.setProperty('--sg-result-stagger', `${Math.min(index * 24, 168)}ms`);
      item.classList.add('sg-predictive-item-ready');
    });
  };

  const cartItemKey = (item) => {
    const link = item.querySelector('.cart-item__name[href], a[href*="/products/"]');
    const options = item.querySelector('.product-option');
    return `${link?.getAttribute('href') || item.id}|${options?.textContent.trim() || ''}`;
  };

  const cartItems = (drawer) => [...drawer.querySelectorAll('.cart-item')];

  const cartDrawersIn = (scope) => {
    const customDrawers = elementsIn(scope, 'cart-drawer');
    return customDrawers.length ? customDrawers : elementsIn(scope, '.cart-drawer');
  };

  const animateCartValues = (drawer) => {
    drawer.querySelectorAll('.cart-item__price-wrapper, .price, .totals__total-value').forEach((value) => {
      value.classList.remove('sg-cart-value-updated');
      void value.offsetWidth;
      value.classList.add('sg-cart-value-updated');
      window.setTimeout(() => value.classList.remove('sg-cart-value-updated'), cssMilliseconds('--sg-motion-fast', 160) + 40);
    });
  };

  const prepareCartDrawer = (drawer) => {
    if (cartStates.has(drawer)) return;
    const state = {
      itemKeys: new Set(cartItems(drawer).map(cartItemKey)),
      pendingAdd: false,
      observer: null,
    };
    state.observer = new MutationObserver(() => {
      const items = cartItems(drawer);
      const nextKeys = new Set(items.map(cartItemKey));
      if (state.pendingAdd) {
        const addedItem = items.find((item) => !state.itemKeys.has(cartItemKey(item)));
        if (addedItem) {
          addedItem.classList.add('sg-cart-line-added');
          window.setTimeout(() => addedItem.classList.remove('sg-cart-line-added'), cssMilliseconds('--sg-motion-ui', 240) + 40);
        }
        state.pendingAdd = false;
      }
      state.itemKeys = nextKeys;
      animateCartValues(drawer);
    });
    state.observer.observe(drawer, { childList: true, characterData: true, subtree: true });
    cartStates.set(drawer, state);
  };

  const prepareUtilityObservers = (scope) => {
    elementsIn(scope, 'header-drawer, predictive-search').forEach((container) => {
      if (utilityObserverStates.has(container)) return;
      const utilityObserver = new MutationObserver(() => {
        prepareMenuMotion(container);
        preparePredictiveResults(container);
      });
      utilityObserver.observe(container, { attributes: true, attributeFilter: ['class', 'open'], childList: true, subtree: true });
      utilityObserverStates.set(container, utilityObserver);
    });
    cartDrawersIn(scope).forEach(prepareCartDrawer);
  };

  const prepareUtilityMotion = (scope) => {
    prepareMenuMotion(scope);
    preparePredictiveResults(scope);
    prepareUtilityObservers(scope);
  };

  const isImageOnlyMedia = (media) => {
    if (!media) return false;
    if (media.querySelector('video, model-viewer, iframe, .deferred-media')) return false;
    return Boolean(media.querySelector('.product__media img, picture img'));
  };

  const prepareProductMedia = (gallery) => {
    if (productMediaStates.has(gallery)) return;

    const readActive = () => gallery.querySelector('.product__media-item.is-active');
    const state = {
      activeId: readActive()?.dataset.mediaId || '',
      indicator: null,
      indicatorHost: null,
      indicatorList: null,
      observer: null,
      resizeObserver: null,
      scroll: null,
      timer: 0,
    };

    gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target] button').forEach((button) => {
      button.dataset.sgProductThumbnail = '';
    });

    const indicatorList = gallery.querySelector('[id^="Slider-Thumbnails"], .thumbnail-list');
    if (indicatorList) {
      state.indicatorList = indicatorList;
      state.indicatorHost = indicatorList.parentElement || indicatorList;
      state.indicatorHost.dataset.sgProductThumbnailHost = '';
      state.indicator = document.createElement('span');
      state.indicator.dataset.sgProductThumbnailIndicator = '';
      state.indicator.setAttribute('aria-hidden', 'true');
      state.indicatorHost.appendChild(state.indicator);
    }

    const updateIndicator = () => {
      if (!state.indicator || !state.indicatorList || !state.indicatorHost) return;
      const activeButton = state.indicatorList.querySelector('.thumbnail[aria-current="true"]');
      if (!activeButton) {
        state.indicator.hidden = true;
        return;
      }
      const buttonRect = activeButton.getBoundingClientRect();
      const hostRect = state.indicatorHost.getBoundingClientRect();
      state.indicator.hidden = false;
      state.indicator.style.width = `${activeButton.offsetWidth}px`;
      state.indicator.style.height = `${activeButton.offsetHeight}px`;
      state.indicator.style.borderColor = window.getComputedStyle(activeButton).borderTopColor;
      state.indicator.style.transform = `translate3d(${buttonRect.left - hostRect.left}px, ${buttonRect.top - hostRect.top}px, 0)`;
      requestAnimationFrame(() => state.indicator?.classList.add('is-ready'));
    };
    updateIndicator();
    if (indicatorList) {
      state.scroll = updateIndicator;
      indicatorList.addEventListener('scroll', state.scroll, { passive: true });
    }
    if ('ResizeObserver' in window && indicatorList) {
      state.resizeObserver = new ResizeObserver(updateIndicator);
      state.resizeObserver.observe(indicatorList);
    }

    state.observer = new MutationObserver(() => {
      updateIndicator();
      const active = readActive();
      const activeId = active?.dataset.mediaId || '';
      if (!activeId || activeId === state.activeId) return;
      state.activeId = activeId;
      if (!canAnimate() || !isImageOnlyMedia(active)) return;

      active.classList.remove('sg-product-media-switching');
      void active.offsetWidth;
      active.classList.add('sg-product-media-switching');
      if (state.timer) window.clearTimeout(state.timer);
      state.timer = window.setTimeout(() => {
        active.classList.remove('sg-product-media-switching');
        state.timer = 0;
      }, cssMilliseconds('--sg-motion-ui', 240) + 40);
    });
    state.observer.observe(gallery, {
      attributes: true,
      attributeFilter: ['aria-current', 'class'],
      subtree: true,
    });
    productMediaStates.set(gallery, state);
  };

  const resetPendingProduct = (root) => {
    const state = productStates.get(root);
    if (!state) return;
    if (state.pendingTimer) window.clearTimeout(state.pendingTimer);
    state.pending = false;
    state.pendingButton = null;
    state.pendingVariantId = '';
    state.pendingTimer = 0;
    pendingProductRoots.delete(root);
  };

  const productLabel = (button) => {
    if (!button) return null;
    return [...button.children].find((child) => (
      child.matches?.('span:not(.sold-out-message):not(.loading__spinner)') &&
      !child.classList.contains('hidden')
    )) || button.querySelector('span:not(.sold-out-message):not(.loading__spinner)');
  };

  const showProductAddSuccess = (event = {}) => {
    const eventVariantId = String(event.productVariantId || event.detail?.productVariantId || '');
    [...pendingProductRoots].forEach((root) => {
      const state = productStates.get(root);
      if (!state?.pending) return;
      if (eventVariantId && state.pendingVariantId && eventVariantId !== state.pendingVariantId) return;

      const button = state.pendingButton;
      const label = productLabel(button);
      if (!button || !label) {
        resetPendingProduct(root);
        return;
      }

      const token = ++state.successToken;
      const originalLabel = productButtonLabels.get(button) || label.textContent;
      if (!productButtonLabels.has(button)) productButtonLabels.set(button, originalLabel);
      label.textContent = 'Added ✓';
      if (canAnimate()) {
        button.classList.remove('sg-product-added');
        void button.offsetWidth;
        button.classList.add('sg-product-added');
      }
      resetPendingProduct(root);

      if (state.restoreTimer) window.clearTimeout(state.restoreTimer);
      state.restoreTimer = window.setTimeout(() => {
        if (state.successToken !== token) return;
        label.textContent = originalLabel;
        button.classList.remove('sg-product-added');
        state.restoreTimer = 0;
      }, 1200);
    });
  };

  const handleProductAddError = (event = {}) => {
    const eventVariantId = String(event.productVariantId || event.detail?.productVariantId || '');
    [...pendingProductRoots].forEach((root) => {
      const state = productStates.get(root);
      if (!state?.pending) return;
      if (eventVariantId && state.pendingVariantId && eventVariantId !== state.pendingVariantId) return;
      resetPendingProduct(root);
    });
  };

  const prepareProductSubscriptions = () => {
    if (productCartSubscription || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (PUB_SUB_EVENTS.cartUpdate) productCartSubscription = subscribe(PUB_SUB_EVENTS.cartUpdate, handleConfirmedAdd);
    if (PUB_SUB_EVENTS.cartError) productCartErrorSubscription = subscribe(PUB_SUB_EVENTS.cartError, handleProductAddError);
  };

  const prepareProduct = (root) => {
    if (productStates.has(root)) return;

    const media = [...root.querySelectorAll('[data-sg-product-part="media"]')];
    const info = root.querySelector('[data-sg-product-part="info"]');
    if (!media.length || !info) return;

    info.classList.remove('scroll-trigger--offscreen');
    const state = {
      change: null,
      entranceTimer: 0,
      errorObserver: null,
      pending: false,
      pendingButton: null,
      pendingTimer: 0,
      pendingVariantId: '',
      restoreTimer: 0,
      successToken: 0,
    };
    productStates.set(root, state);

    root.querySelectorAll('media-gallery').forEach(prepareProductMedia);
    root.querySelectorAll('product-modal.product-media-modal').forEach((modal) => {
      modal.dataset.sgProductLightbox = '';
    });

    state.change = (event) => {
      if (!event.target.matches('input[type="radio"], select, [role="radio"]')) return;
      if (!event.target.closest('variant-radios, variant-selects, .product-form__input')) return;
      const control = event.target.closest('.product-form__input, variant-selects, variant-radios, .select') || event.target;
      control.classList.remove('sg-variant-feedback');
      void control.offsetWidth;
      control.classList.add('sg-variant-feedback');
      window.setTimeout(() => control.classList.remove('sg-variant-feedback'), cssMilliseconds('--sg-motion-fast', 160) + 30);
    };
    root.addEventListener('change', state.change);

    state.errorObserver = new MutationObserver(() => {
      const visibleError = root.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (visibleError) resetPendingProduct(root);
    });
    state.errorObserver.observe(root, {
      attributes: true,
      attributeFilter: ['hidden'],
      childList: true,
      subtree: true,
    });

    // Product content is hidden only after reusable media/info roles have been mapped successfully.
    if (!canAnimate() || window.Shopify?.designMode) {
      root.classList.add('sg-product-entered');
      return;
    }
    root.classList.add('sg-product-load-ready');
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('sg-product-entered')));
    state.entranceTimer = window.setTimeout(() => {
      root.classList.remove('sg-product-load-ready');
      state.entranceTimer = 0;
    }, cssMilliseconds('--sg-motion-reveal', 500) + 180);
  };

  const prepareProducts = (scope) => {
    elementsIn(scope, '[data-sg-product]').forEach(prepareProduct);
    prepareProductSubscriptions();
  };

  const prepareRecentList = (list) => {
    if (recentListStates.has(list)) return;
    const state = { observer: null, timers: new Set() };
    list.querySelectorAll('[data-sg-recent-item]').forEach((item) => item.dataset.sgRecentKnown = 'true');
    state.observer = new MutationObserver((records) => {
      let index = 0;
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        const items = node.matches('[data-sg-recent-item]') ? [node] : [...node.querySelectorAll('[data-sg-recent-item]')];
        items.forEach((item) => {
          if (item.dataset.sgRecentKnown === 'true') return;
          item.dataset.sgRecentKnown = 'true';
          if (!canAnimate()) return;
          item.style.setProperty('--sg-product-delay', `${Math.min(index * 70, 280)}ms`);
          item.classList.add('sg-recent-item-in');
          const timer = window.setTimeout(() => {
            item.classList.remove('sg-recent-item-in');
            state.timers.delete(timer);
          }, cssMilliseconds('--sg-motion-reveal', 500) + Math.min(index * 70, 280) + 40);
          state.timers.add(timer);
          index += 1;
        });
      }));
    });
    state.observer.observe(list, { childList: true, subtree: true });
    recentListStates.set(list, state);
  };

  const prepareRecentlyViewed = (scope) => {
    elementsIn(scope, '[data-sg-recently-viewed-list]').forEach(prepareRecentList);
  };

  const collectionHeaderParts = (header) => {
    const mapped = [...header.querySelectorAll('[data-sg-collection-header-part]')];
    if (mapped.length) return mapped;
    return [
      header.querySelector('.breadcrumb, .breadcrumbs, nav[aria-label*="readcrumb"]'),
      header.querySelector('.collection-hero__title, h1'),
      header.querySelector('.collection-hero__description, .rte'),
    ].filter(Boolean);
  };

  const prepareCollectionHeader = (header) => {
    if (collectionHeaderStates.has(header)) return;
    const parts = collectionHeaderParts(header);
    if (!parts.length) return;
    parts.forEach((part, index) => {
      part.dataset.sgCollectionHeaderPart ||= ['breadcrumb', 'title', 'description'][index] || 'copy';
      part.style.setProperty('--sg-collection-delay', `${Math.min(index * 65, 130)}ms`);
    });
    const state = { timer: 0 };
    collectionHeaderStates.set(header, state);

    // A restored history entry stays final so product-card and scroll restoration feel native.
    if (!canAnimate() || initialNavigationWasBackForward || window.Shopify?.designMode) {
      header.classList.add('sg-collection-header-entered');
      return;
    }
    header.classList.add('sg-collection-header-ready');
    requestAnimationFrame(() => requestAnimationFrame(() => header.classList.add('sg-collection-header-entered')));
    state.timer = window.setTimeout(() => {
      header.classList.remove('sg-collection-header-ready');
      state.timer = 0;
    }, cssMilliseconds('--sg-motion-reveal', 500) + 220);
  };

  const resultCardsFor = (grid) => [...grid.children].filter((child) => (
    child.matches('[data-sg-result-card], .sg-product-card, .grid__item') ||
    child.querySelector('[data-sg-result-card]')
  ));

  const animateResultCards = (grid, cards, startIndex = 0) => {
    const state = resultGridStates.get(grid);
    if (!state) return;
    cards.forEach((card, index) => {
      if (state.known.has(card)) return;
      state.known.add(card);
      card.dataset.sgResultCard = '';
      if (!canAnimate() || initialNavigationWasBackForward || window.Shopify?.designMode) return;
      const delay = Math.min((startIndex + index) * 30, 330);
      card.style.setProperty('--sg-result-delay', `${delay}ms`);
      card.classList.add('sg-result-card-ready');
      card.querySelectorAll('[data-sg-sale-badge]').forEach((badge) => badge.classList.add('sg-sale-badge-ready'));
      requestAnimationFrame(() => requestAnimationFrame(() => card.classList.add('sg-result-card-entered')));
      const timer = window.setTimeout(() => {
        card.classList.remove('sg-result-card-ready', 'sg-result-card-entered');
        card.querySelectorAll('[data-sg-sale-badge]').forEach((badge) => badge.classList.remove('sg-sale-badge-ready'));
        state.timers.delete(timer);
      }, cssMilliseconds('--sg-motion-reveal', 500) + delay + 80);
      state.timers.add(timer);
    });
  };

  const prepareResultGrid = (grid, initial) => {
    if (resultGridStates.has(grid)) return;
    grid.dataset.sgResultsGrid = '';
    const state = { known: new WeakSet(), observer: null, timers: new Set() };
    resultGridStates.set(grid, state);
    const cards = resultCardsFor(grid);
    const animated = initial ? cards.slice(0, 12) : [];
    animateResultCards(grid, animated);
    cards.slice(animated.length).forEach((card) => {
      state.known.add(card);
      card.dataset.sgResultCard = '';
    });

    state.observer = new MutationObserver((records) => {
      const added = [];
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (!(node instanceof Element) || node.parentElement !== grid) return;
        if (node.matches('[data-sg-result-card], .sg-product-card, .grid__item') || node.querySelector('[data-sg-result-card]')) added.push(node);
      }));
      // Load-more cards are new; existing cards are never replayed and transforms do not affect scroll position.
      animateResultCards(grid, added);
    });
    state.observer.observe(grid, { childList: true });
  };

  const resultGridsIn = (scope) => elementsIn(scope, [
    '[data-sg-results-grid]',
    '#ProductGridContainer .product-grid',
    '#product-grid',
    '.template-search__results .product-grid',
  ].join(', '));

  const prepareResultsContainer = (container) => {
    if (resultsContainerStates.has(container)) return;
    const state = { observer: null };
    resultsContainerStates.set(container, state);
    state.observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node instanceof Element) resultGridsIn(node).forEach((grid) => prepareResultGrid(grid, false));
      }));
    });
    state.observer.observe(container, { childList: true, subtree: true });
  };

  const prepareAppliedChips = (scope, animate) => {
    elementsIn(scope, '.active-facets__button, facet-remove .active-facets__button').forEach((chip) => {
      if (filterChipKnown.has(chip)) return;
      filterChipKnown.add(chip);
      if (!animate || !canAnimate()) return;
      chip.classList.add('sg-filter-chip-in');
      window.setTimeout(() => chip.classList.remove('sg-filter-chip-in'), cssMilliseconds('--sg-motion-ui', 240) + 40);
    });
  };

  const updateGalleryIndicator = (section) => {
    const state = galleryFilterStates.get(section);
    const active = section.querySelector('.sg-gallery-page__filter.is-active');
    if (!state || !active) return;
    const hostRect = state.host.getBoundingClientRect();
    const buttonRect = active.getBoundingClientRect();
    state.indicator.style.width = `${buttonRect.width}px`;
    state.indicator.style.transform = `translate3d(${buttonRect.left - hostRect.left + state.host.scrollLeft}px, ${buttonRect.bottom - hostRect.top + 3}px, 0)`;
  };

  const prepareGalleryFilters = (section) => {
    if (galleryFilterStates.has(section)) return;
    const host = section.querySelector('[data-sg-gallery-filter-list], .sg-gallery-page__filter-list');
    if (!host) return;
    const indicator = document.createElement('span');
    indicator.className = 'sg-gallery-page__active-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    host.append(indicator);
    const state = { host, indicator, outTimer: 0, inTimer: 0, resizeObserver: null, scroll: null };
    galleryFilterStates.set(section, state);
    state.scroll = () => updateGalleryIndicator(section);
    host.addEventListener('scroll', state.scroll, { passive: true });
    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(state.scroll);
      state.resizeObserver.observe(host);
    }
    requestAnimationFrame(state.scroll);
  };

  const filterGallery = (section, button) => {
    prepareGalleryFilters(section);
    const state = galleryFilterStates.get(section);
    const buttons = [...section.querySelectorAll('.sg-gallery-page__filter')];
    const cards = [...section.querySelectorAll('.sg-gallery-page__card')];
    if (!state || !buttons.length || !cards.length) return false;
    if (state.outTimer) window.clearTimeout(state.outTimer);
    if (state.inTimer) window.clearTimeout(state.inTimer);
    cards.forEach((card) => card.classList.remove('sg-gallery-filtering-out', 'sg-gallery-filtering-in'));
    buttons.forEach((nextButton) => {
      const active = nextButton === button;
      nextButton.classList.toggle('is-active', active);
      nextButton.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    requestAnimationFrame(() => updateGalleryIndicator(section));
    const filter = button.dataset.filter || 'all';
    const apply = () => cards.forEach((card) => {
      const category = card.dataset.category || 'all';
      card.hidden = filter !== 'all' && category !== filter;
    });
    if (!canAnimate()) {
      apply();
      return true;
    }
    cards.filter((card) => !card.hidden).forEach((card) => card.classList.add('sg-gallery-filtering-out'));
    state.outTimer = window.setTimeout(() => {
      apply();
      cards.forEach((card) => card.classList.remove('sg-gallery-filtering-out'));
      const visible = cards.filter((card) => !card.hidden);
      visible.forEach((card) => card.classList.add('sg-gallery-filtering-in'));
      state.inTimer = window.setTimeout(() => {
        visible.forEach((card) => card.classList.remove('sg-gallery-filtering-in'));
        state.inTimer = 0;
      }, 220);
      state.outTimer = 0;
    }, 120);
    return true;
  };

  const prepareCollectionMotion = (scope, initial = true) => {
    elementsIn(scope, '[data-sg-collection-header], .collection-hero').forEach(prepareCollectionHeader);
    elementsIn(scope, '#ProductGridContainer, .template-search__results').forEach(prepareResultsContainer);
    resultGridsIn(scope).forEach((grid) => prepareResultGrid(grid, initial));
    prepareAppliedChips(scope, !initial);
    elementsIn(scope, '[data-sg-gallery-page]').forEach(prepareGalleryFilters);
  };

  const appBoundarySelector = 'iframe, .shopify-app-block, .shopify-block, [data-shopify-editor-block*="@app"]';
  const insideAppBoundary = (element) => Boolean(element.closest?.('.shopify-app-block, .shopify-block, [data-shopify-editor-block*="@app"]'));
  const containsAppBoundary = (element) => Boolean(element.querySelector?.(appBoundarySelector));

  const getSecondaryObserver = () => {
    if (secondaryObserver || !('IntersectionObserver' in window)) return secondaryObserver;
    secondaryObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const group = entry.target;
        const state = secondaryMotionStates.get(group);
        group.classList.add('sg-secondary-visible');
        secondaryObserver.unobserve(group);
        if (state) {
          state.timer = window.setTimeout(() => {
            group.classList.remove('sg-secondary-ready');
            state.timer = 0;
          }, cssMilliseconds('--sg-motion-reveal', 500) + 420);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
    return secondaryObserver;
  };

  const markSecondaryItem = (element, role, index = 0) => {
    if (!element || insideAppBoundary(element)) return;
    element.dataset.sgSecondaryPart = role;
    element.style.setProperty('--sg-secondary-delay', `${Math.min(index * 65, 390)}ms`);
  };

  const prepareSecondaryGroup = (group, name, mapper, allowEntrance = true) => {
    if (!group || secondaryMotionStates.has(group) || insideAppBoundary(group) || containsAppBoundary(group)) return;
    group.dataset.sgSecondaryGroup = name;
    mapper(group);
    const state = { timer: 0 };
    secondaryMotionStates.set(group, state);
    if (!allowEntrance || !canAnimate() || !('IntersectionObserver' in window) || initialNavigationWasBackForward || window.Shopify?.designMode) {
      group.classList.add('sg-secondary-visible');
      return;
    }
    // Secondary content is hidden only after safe, non-app roles have been mapped successfully.
    group.classList.add('sg-secondary-ready');
    getSecondaryObserver().observe(group);
  };

  const prepareAboutMotion = (scope) => {
    elementsIn(scope, '.sg-about-page').forEach((page) => {
      const hero = page.querySelector('.sg-about-hero');
      prepareSecondaryGroup(hero, 'about-hero', () => markSecondaryItem(hero?.querySelector('.sg-about-copy'), 'copy'));

      const founder = page.querySelector('.sg-about-founder');
      prepareSecondaryGroup(founder, 'about-founder', () => {
        founder.dataset.sgSecondaryFounder = '';
        markSecondaryItem(founder.querySelector(':scope > div'), 'story');
      });

      const products = page.querySelector('.sg-products-section');
      prepareSecondaryGroup(products, 'about-products', () => {
        products.querySelectorAll('.sg-product-card-grid > .sg-product-card, .sg-card-grid > .sg-card').forEach((card, index) => markSecondaryItem(card, 'category-card', index));
      });

      const proof = page.querySelector('.sg-dark-band');
      prepareSecondaryGroup(proof, 'about-proof', () => {
        proof.querySelectorAll('.sg-feature-grid > div').forEach((card, index) => markSecondaryItem(card, 'proof-card', index));
      });

      const steps = page.querySelector('.sg-steps');
      prepareSecondaryGroup(steps, 'about-steps', () => {
        steps.querySelectorAll(':scope > div').forEach((step, index) => markSecondaryItem(step, 'step', index));
      });

      const local = page.querySelector('.sg-about-section.sg-local');
      prepareSecondaryGroup(local, 'about-local', () => markSecondaryItem(local, 'content-group'));

      const stats = page.querySelector('.sg-stats-grid');
      prepareSecondaryGroup(stats, 'about-stats', () => {
        stats.querySelectorAll(':scope > div').forEach((card, index) => markSecondaryItem(card, 'stat-card', index));
        stats.querySelectorAll('strong').forEach((value) => {
          const numeric = value.textContent.match(/-?[\d,.]+/);
          if (numeric) value.dataset.sgCount = numeric[0];
        });
      });

      const cta = page.querySelector('.sg-about-cta');
      prepareSecondaryGroup(cta, 'about-cta', () => markSecondaryItem(cta, 'content-group'));
    });
  };

  const prepareStudioMotion = (scope) => {
    elementsIn(scope, '#sign-studio-seo-intro').forEach((intro) => {
      // Only the iframe's sibling intro is mapped; the customizer and its fixed ancestor are never transformed.
      prepareSecondaryGroup(intro, 'studio-intro', () => {
        markSecondaryItem(intro.querySelector('h1'), 'studio-heading');
        markSecondaryItem(intro.querySelector('p'), 'studio-copy', 1);
        intro.querySelectorAll('nav a').forEach((link, index) => markSecondaryItem(link, 'studio-link', index + 2));
      });
    });
  };

  const cartPageLineKey = (line) => {
    const link = line.querySelector('.cart-item__name[href], a[href*="/products/"]');
    const options = line.querySelector('.sg-cart-options, .product-option');
    return `${link?.getAttribute('href') || line.id}|${options?.textContent.trim() || ''}`;
  };

  const cartPageSnapshot = (line) => ({
    price: line.querySelector('[data-sg-cart-line-price], .sg-cart-price-cell')?.textContent.trim() || '',
    total: line.querySelector('[data-sg-cart-line-total], .sg-cart-total-cell')?.textContent.trim() || '',
  });

  const animateCartValue = (element) => {
    if (!element || !canAnimate()) return;
    element.classList.remove('sg-cart-value-updated');
    void element.offsetWidth;
    element.classList.add('sg-cart-value-updated');
    window.setTimeout(() => element.classList.remove('sg-cart-value-updated'), cssMilliseconds('--sg-motion-fast', 160) + 40);
  };

  const scanCartPage = (root, initial) => {
    const state = cartPageStates.get(root);
    if (!state) return;
    const lines = [...root.querySelectorAll('[data-sg-cart-line], .cart-item')];
    const nextKeys = new Set();
    lines.forEach((line, index) => {
      line.dataset.sgCartLine = '';
      const key = cartPageLineKey(line);
      nextKeys.add(key);
      const previous = cartPageSnapshots.get(key);
      const current = cartPageSnapshot(line);
      if (!initial && !cartPageKnownKeys.has(key) && canAnimate()) {
        line.style.setProperty('--sg-cart-line-delay', `${Math.min(index * 45, 180)}ms`);
        line.classList.add('sg-cart-page-line-added');
        window.setTimeout(() => line.classList.remove('sg-cart-page-line-added'), cssMilliseconds('--sg-motion-ui', 240) + 220);
      } else if (!initial && previous) {
        if (previous.price !== current.price) animateCartValue(line.querySelector('[data-sg-cart-line-price], .sg-cart-price-cell'));
        if (previous.total !== current.total) animateCartValue(line.querySelector('[data-sg-cart-line-total], .sg-cart-total-cell'));
      }
      cartPageSnapshots.set(key, current);
    });
    [...cartPageSnapshots.keys()].forEach((key) => {
      if (!nextKeys.has(key)) cartPageSnapshots.delete(key);
    });
    cartPageKnownKeys = nextKeys;

    const empty = root.querySelector('[data-sg-cart-empty], .cart__warnings');
    const isEmpty = !lines.length && Boolean(empty);
    if (!initial && isEmpty && !state.emptyShown && canAnimate()) {
      empty.classList.add('sg-cart-empty-in');
      window.setTimeout(() => empty.classList.remove('sg-cart-empty-in'), cssMilliseconds('--sg-motion-reveal', 500) + 40);
    }
    state.emptyShown = isEmpty;
  };

  const prepareCartPage = (root, initial) => {
    if (cartPageStates.has(root) || root.closest('cart-drawer, .cart-drawer')) return;
    const state = { observer: null, frame: 0, click: null, timers: new Set(), emptyShown: false };
    cartPageStates.set(root, state);
    prepareSecondaryGroup(root, 'cart-page', () => {
      markSecondaryItem(root.querySelector('[data-sg-cart-heading], .sg-cart-heading-row, h1'), 'cart-heading');
      root.querySelectorAll('[data-sg-cart-line], .cart-item').forEach((line, index) => markSecondaryItem(line, 'cart-line', index));
      markSecondaryItem(root.querySelector('[data-sg-cart-empty], .cart__warnings'), 'cart-empty');
    }, initial);
    scanCartPage(root, initial);

    state.click = (event) => {
      const remove = event.target.closest('cart-remove-button a, [data-cart-remove], .sg-cart-remove-button');
      if (!remove) return;
      const line = remove.closest('[data-sg-cart-line], .cart-item');
      if (!line || !canAnimate()) return;
      line.classList.add('sg-cart-line-removing');
      const timer = window.setTimeout(() => {
        line.classList.add('sg-cart-line-collapsing');
        state.timers.delete(timer);
      }, 100);
      state.timers.add(timer);
    };
    root.addEventListener('click', state.click, true);
    state.observer = new MutationObserver(() => {
      if (state.frame) return;
      state.frame = requestAnimationFrame(() => {
        state.frame = 0;
        scanCartPage(root, false);
      });
    });
    state.observer.observe(root, { attributes: true, attributeFilter: ['class'], childList: true, characterData: true, subtree: true });
  };

  const prepareCartFooter = (footer) => {
    if (cartFooterStates.has(footer)) return;
    const readSubtotal = () => footer.querySelector('[data-sg-cart-subtotal], .totals__total-value');
    const initialValue = readSubtotal();
    if (initialValue) {
      const initialText = initialValue.textContent.trim();
      if (cartPageSubtotalText && initialText !== cartPageSubtotalText) animateCartValue(initialValue);
      cartPageSubtotalText = initialText;
    }
    const state = { observer: null, frame: 0 };
    cartFooterStates.set(footer, state);
    state.observer = new MutationObserver(() => {
      if (state.frame) return;
      state.frame = requestAnimationFrame(() => {
        state.frame = 0;
        const subtotal = readSubtotal();
        const next = subtotal?.textContent.trim() || '';
        if (next && cartPageSubtotalText && next !== cartPageSubtotalText) animateCartValue(subtotal);
        if (next) cartPageSubtotalText = next;
      });
    });
    state.observer.observe(footer, { childList: true, characterData: true, subtree: true });
  };

  const statusVisible = (status) => !status.hidden && status.textContent.trim() && window.getComputedStyle(status).display !== 'none';

  const scanFormStatuses = (form) => {
    const successSelector = '[data-sg-discount-applied], .form__message--success, .form-status.form-status-list.form__message, .note--success, [role="status"].form-status, [data-form-status="success"]';
    const errorSelector = '[data-sg-discount-error], .errors, .form-status-list:not(.form-status.form__message), .form__message:not(.form__message--success):not([role="status"]), [data-form-status="error"]';
    form.querySelectorAll(successSelector).forEach((status) => {
      if (formStatusSeen.has(status) || !statusVisible(status)) return;
      formStatusSeen.add(status);
      if (status.querySelector('svg, .svg-wrapper')) status.classList.add('sg-form-success-has-icon');
      status.classList.add('sg-form-success-once');
    });
    form.querySelectorAll(errorSelector).forEach((status) => {
      if (formStatusSeen.has(status) || !statusVisible(status)) return;
      formStatusSeen.add(status);
      if (!canAnimate()) return;
      status.classList.add('sg-form-error-once');
      window.setTimeout(() => status.classList.remove('sg-form-error-once'), cssMilliseconds('--sg-motion-ui', 240) + 40);
    });
  };

  const prepareForm = (form) => {
    if (formStates.has(form) || insideAppBoundary(form)) return;
    form.dataset.sgFormMotion = '';
    const state = { observer: null };
    formStates.set(form, state);
    scanFormStatuses(form);
    state.observer = new MutationObserver(() => scanFormStatuses(form));
    state.observer.observe(form, { attributes: true, attributeFilter: ['class', 'hidden'], childList: true, characterData: true, subtree: true });
  };

  const prepareContentPageMotion = (scope) => {
    if (!(document.body.className.includes('template-page') || window.location.pathname.startsWith('/pages/'))) return;
    if (document.querySelector('.sg-about-page, #sign-studio-fullscreen, [data-sg-gallery-page]')) return;
    const legalPage = window.location.pathname.startsWith('/policies/') || /privacy|terms|refund|legal|accessibility/i.test(window.location.pathname);
    elementsIn(scope, '#MainContent .main-page-title, #MainContent .page-title, #MainContent .shopify-policy__title').slice(0, 1).forEach((title) => {
      prepareSecondaryGroup(title, legalPage ? 'policy-title' : 'content-title', () => markSecondaryItem(title, 'content-title'));
    });
    if (legalPage) return;
    elementsIn(scope, '#MainContent .main-page .rte, #MainContent .page-width--narrow > .rte').slice(0, 2).forEach((content) => {
      if (containsAppBoundary(content) || content.querySelector('table')) return;
      prepareSecondaryGroup(content, 'content-group', () => markSecondaryItem(content, 'content-group'));
    });
  };

  const prepareSecondaryMotion = (scope, initial = true) => {
    prepareAboutMotion(scope);
    prepareStudioMotion(scope);
    elementsIn(scope, '[data-sg-cart-page], cart-items.sg-cart-items').forEach((root) => prepareCartPage(root, initial));
    elementsIn(scope, '[data-sg-cart-footer], #main-cart-footer').forEach(prepareCartFooter);
    elementsIn(scope, 'form').forEach(prepareForm);
    prepareContentPageMotion(scope);
  };

  const showFinal = (scope = document) => {
    elementsIn(scope, '[data-sg-header-entrance]').forEach((header) => {
      const state = headerStates.get(header);
      if (state?.timer) window.clearTimeout(state.timer);
      if (state) state.timer = 0;
      header.classList.remove('sg-header-entrance-ready');
      header.classList.add('sg-header-entered');
    });
    elementsIn(scope, '[data-sg-hero]').forEach((hero) => {
      const state = heroStates.get(hero);
      if (state?.timer) window.clearTimeout(state.timer);
      if (state?.trustGlowTimer) window.clearTimeout(state.trustGlowTimer);
      if (state) {
        state.entranceEndsAt = 0;
        state.timer = 0;
        state.trustGlowPlayed = true;
        state.trustGlowQueued = false;
        state.trustGlowTimer = 0;
        if (state.trust) heroTrustObserver?.unobserve(state.trust);
      }
      hero.classList.remove('sg-hero-entrance-ready', 'sg-hero-trust-glow-sequence');
      hero.classList.add('sg-hero-entered');
    });
    elementsIn(scope, revealSelector).forEach((element) => {
      observer?.unobserve(element);
      element.classList.remove('sg-motion-pending', 'sg-motion-animating', 'scroll-trigger--offscreen');
      element.classList.add('sg-motion-revealed');
      if (element.hasAttribute('data-sg-count')) finishCount(element);
    });
    elementsIn(scope, '[data-sg-section]').forEach((section) => {
      sectionObserver?.unobserve(section);
      const state = sectionMotionStates.get(section);
      if (state?.timer) window.clearTimeout(state.timer);
      if (state) {
        state.timer = 0;
        state.glowPlayed = true;
        if (state.glowTarget) sectionGlowObserver?.unobserve(state.glowTarget);
      }
      section.classList.remove('sg-section-motion-ready');
      section.classList.add('sg-section-visible', 'sg-section-glow-visible');
    });
    elementsIn(scope, '[data-sg-product]').forEach((product) => {
      const state = productStates.get(product);
      if (state?.entranceTimer) window.clearTimeout(state.entranceTimer);
      if (state) state.entranceTimer = 0;
      product.classList.remove('sg-product-load-ready');
      product.classList.add('sg-product-entered');
    });
    elementsIn(scope, '[data-sg-recent-item]').forEach((item) => item.classList.remove('sg-recent-item-in'));
    elementsIn(scope, '[data-sg-collection-header], .collection-hero').forEach((header) => {
      const state = collectionHeaderStates.get(header);
      if (state?.timer) window.clearTimeout(state.timer);
      header.classList.remove('sg-collection-header-ready');
      header.classList.add('sg-collection-header-entered');
    });
    resultGridsIn(scope).forEach((grid) => resultCardsFor(grid).forEach((card) => {
      card.classList.remove('sg-result-card-ready', 'sg-result-card-entered');
      card.querySelectorAll('[data-sg-sale-badge]').forEach((badge) => badge.classList.remove('sg-sale-badge-ready'));
    }));
    elementsIn(scope, '.sg-gallery-page__card').forEach((card) => card.classList.remove('sg-gallery-filtering-out', 'sg-gallery-filtering-in'));
    elementsIn(scope, '[data-sg-secondary-group]').forEach((group) => {
      secondaryObserver?.unobserve(group);
      const state = secondaryMotionStates.get(group);
      if (state?.timer) window.clearTimeout(state.timer);
      group.classList.remove('sg-secondary-ready');
      group.classList.add('sg-secondary-visible');
    });
    elementsIn(scope, '[data-sg-cart-line], .sg-cart-line').forEach((line) => line.classList.remove('sg-cart-page-line-added', 'sg-cart-line-removing', 'sg-cart-line-collapsing'));
    elementsIn(scope, '.sg-form-error-once').forEach((status) => status.classList.remove('sg-form-error-once'));
    resetDepth(scope);
  };

  const initialize = (scope = document) => {
    prepareHeaderEntrance(scope);
    prepareHeroes(scope);
    prepareSections(scope);
    prepareProducts(scope);
    prepareRecentlyViewed(scope);
    prepareCollectionMotion(scope);
    prepareSecondaryMotion(scope);
    watchLateSections();
    if (!canAnimate() || !('IntersectionObserver' in window)) {
      showFinal(scope);
      return;
    }

    prepareStaggers(scope);
    elementsIn(scope, revealSelector).forEach(prepareReveal);
    prepareDepthIn(scope);
    prepareUtilityMotion(scope);
    rootElement.classList.remove('sg-motion-failed');
    rootElement.classList.add('sg-motion-ready');
  };

  const recoverFinalState = (scope = document) => {
    rootElement.classList.remove('sg-motion-ready');
    rootElement.classList.add('sg-motion-failed');
    try {
      showFinal(scope);
      return;
    } catch (_) {
      // If initialization itself fails, removing every opt-in hidden class keeps the storefront usable.
    }
    try {
      const target = scope.querySelectorAll ? scope : document;
      target.querySelectorAll([
        '.sg-motion-pending',
        '.scroll-trigger--offscreen',
        '.sg-header-entrance-ready',
        '.sg-hero-entrance-ready',
        '.sg-section-motion-ready',
        '.sg-product-load-ready',
        '.sg-collection-header-ready',
        '.sg-result-card-ready',
        '.sg-secondary-ready',
      ].join(',')).forEach((element) => {
        element.classList.remove(
          'sg-motion-pending',
          'scroll-trigger--offscreen',
          'sg-header-entrance-ready',
          'sg-hero-entrance-ready',
          'sg-section-motion-ready',
          'sg-product-load-ready',
          'sg-collection-header-ready',
          'sg-result-card-ready',
          'sg-secondary-ready'
        );
        const countState = countStates.get(element);
        if (countState) element.textContent = countState.finalText;
      });
      target.querySelectorAll('[data-sg-hero-depth-layer]').forEach((layer) => layer.style.transform = '');
    } catch (_) {
      // The failed root class supplies a CSS-only final-state fallback if DOM traversal is unavailable.
    }
  };

  const initializeSafely = (scope = document) => {
    try {
      initialize(scope);
    } catch (_) {
      recoverFinalState(scope);
    }
  };

  const cleanup = (scope) => {
    elementsIn(scope, '[data-sg-header-entrance]').forEach((header) => {
      const state = headerStates.get(header);
      if (state?.timer) window.clearTimeout(state.timer);
      headerStates.delete(header);
    });
    elementsIn(scope, revealSelector).forEach((element) => {
      observer?.unobserve(element);
      initialized.delete(element);
      finishCount(element);
    });
    elementsIn(scope, '[data-sg-hero-depth]').forEach((container) => {
      const state = depthStates.get(container);
      if (!state) return;
      depthObserver?.unobserve(container);
      container.removeEventListener('pointermove', state.move);
      container.removeEventListener('pointerleave', state.leave);
      if (state.frame) cancelAnimationFrame(state.frame);
      depthStates.delete(container);
    });
    elementsIn(scope, '[data-sg-hero]').forEach((hero) => {
      const state = heroStates.get(hero);
      if (state?.timer) window.clearTimeout(state.timer);
      if (state?.trustGlowTimer) window.clearTimeout(state.trustGlowTimer);
      if (state?.trust) heroTrustObserver?.unobserve(state.trust);
      heroStates.delete(hero);
    });
    elementsIn(scope, '[data-sg-section]').forEach((section) => {
      sectionObserver?.unobserve(section);
      const state = sectionMotionStates.get(section);
      if (state?.timer) window.clearTimeout(state.timer);
      if (state?.glowTarget) sectionGlowObserver?.unobserve(state.glowTarget);
      sectionMotionStates.delete(section);
    });
    elementsIn(scope, 'header-drawer, predictive-search').forEach((container) => {
      utilityObserverStates.get(container)?.disconnect();
      utilityObserverStates.delete(container);
    });
    elementsIn(scope, '[data-sg-product]').forEach((product) => {
      const state = productStates.get(product);
      if (!state) return;
      if (state.entranceTimer) window.clearTimeout(state.entranceTimer);
      if (state.restoreTimer) window.clearTimeout(state.restoreTimer);
      if (state.pendingTimer) window.clearTimeout(state.pendingTimer);
      state.errorObserver?.disconnect();
      product.removeEventListener('change', state.change);
      pendingProductRoots.delete(product);
      product.querySelectorAll('media-gallery').forEach((gallery) => {
        const mediaState = productMediaStates.get(gallery);
        if (mediaState?.timer) window.clearTimeout(mediaState.timer);
        mediaState?.observer?.disconnect();
        mediaState?.resizeObserver?.disconnect();
        if (mediaState?.indicatorList && mediaState.scroll) mediaState.indicatorList.removeEventListener('scroll', mediaState.scroll);
        productMediaStates.delete(gallery);
      });
      productStates.delete(product);
    });
    elementsIn(scope, '[data-sg-recently-viewed-list]').forEach((list) => {
      const state = recentListStates.get(list);
      state?.observer?.disconnect();
      state?.timers?.forEach((timer) => window.clearTimeout(timer));
      recentListStates.delete(list);
    });
    elementsIn(scope, '[data-sg-collection-header], .collection-hero').forEach((header) => {
      const state = collectionHeaderStates.get(header);
      if (state?.timer) window.clearTimeout(state.timer);
      collectionHeaderStates.delete(header);
    });
    resultGridsIn(scope).forEach((grid) => {
      const state = resultGridStates.get(grid);
      state?.observer?.disconnect();
      state?.timers?.forEach((timer) => window.clearTimeout(timer));
      resultGridStates.delete(grid);
    });
    elementsIn(scope, '#ProductGridContainer, .template-search__results').forEach((container) => {
      resultsContainerStates.get(container)?.observer?.disconnect();
      resultsContainerStates.delete(container);
    });
    elementsIn(scope, '[data-sg-gallery-page]').forEach((section) => {
      const state = galleryFilterStates.get(section);
      if (!state) return;
      if (state.outTimer) window.clearTimeout(state.outTimer);
      if (state.inTimer) window.clearTimeout(state.inTimer);
      state.resizeObserver?.disconnect();
      state.host.removeEventListener('scroll', state.scroll);
      state.indicator.remove();
      galleryFilterStates.delete(section);
    });
    elementsIn(scope, '[data-sg-secondary-group]').forEach((group) => {
      secondaryObserver?.unobserve(group);
      const state = secondaryMotionStates.get(group);
      if (state?.timer) window.clearTimeout(state.timer);
      secondaryMotionStates.delete(group);
    });
    elementsIn(scope, '[data-sg-cart-page], cart-items.sg-cart-items').forEach((root) => {
      const state = cartPageStates.get(root);
      if (!state) return;
      state.observer?.disconnect();
      if (state.frame) cancelAnimationFrame(state.frame);
      state.timers.forEach((timer) => window.clearTimeout(timer));
      root.removeEventListener('click', state.click, true);
      cartPageStates.delete(root);
    });
    elementsIn(scope, '[data-sg-cart-footer], #main-cart-footer').forEach((footer) => {
      const state = cartFooterStates.get(footer);
      state?.observer?.disconnect();
      if (state?.frame) cancelAnimationFrame(state.frame);
      cartFooterStates.delete(footer);
    });
    elementsIn(scope, 'form[data-sg-form-motion]').forEach((form) => {
      formStates.get(form)?.observer?.disconnect();
      formStates.delete(form);
    });
  };

  const handleConfirmedAdd = (event = {}) => {
    const now = performance.now();
    if (now - lastConfirmedAdd < 600) return;
    lastConfirmedAdd = now;
    showProductAddSuccess(event);

    if (!canAnimate()) return;

    cartDrawersIn(document).forEach((drawer) => {
      prepareCartDrawer(drawer);
      cartStates.get(drawer).pendingAdd = true;
    });
    document.querySelectorAll('.cart-count-bubble, .count-bubble').forEach((badge) => {
      badge.classList.remove('sg-cart-badge-pop');
      void badge.offsetWidth;
      badge.classList.add('sg-cart-badge-pop');
      window.setTimeout(() => badge.classList.remove('sg-cart-badge-pop'), cssMilliseconds('--sg-motion-ui', 240) + 40);
    });
  };

  ['ajaxProduct:added', 'product:added'].forEach((eventName) => {
    document.addEventListener(eventName, handleConfirmedAdd);
    window.addEventListener(eventName, handleConfirmedAdd);
  });

  ['ajaxProduct:error', 'product:error'].forEach((eventName) => {
    document.addEventListener(eventName, handleProductAddError);
    window.addEventListener(eventName, handleProductAddError);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-sg-product] form[data-type="add-to-cart-form"]');
    if (!form) return;
    const root = form.closest('[data-sg-product]');
    const state = productStates.get(root);
    const button = event.submitter || form.querySelector('[type="submit"][name="add"], .product-form__submit');
    if (!state || !button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    if (state.pending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    state.pending = true;
    state.pendingButton = button;
    state.pendingVariantId = String(form.querySelector('[name="id"]')?.value || '');
    pendingProductRoots.add(root);
    state.pendingTimer = window.setTimeout(() => resetPendingProduct(root), 12000);

    // App validation can cancel before Shopify starts a request; release that non-submission promptly.
    window.setTimeout(() => {
      if (!state.pending) return;
      const requestStarted = button.classList.contains('loading') || button.getAttribute('aria-disabled') === 'true';
      const visibleError = root.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (!requestStarted || visibleError) resetPendingProduct(root);
    }, 350);
  }, true);

  // One global lifecycle owns observers and editor events, preventing duplicate listeners on section reloads.
  document.addEventListener('shopify:section:load', (event) => initializeSafely(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    showFinal(event.target);
    elementsIn(event.target, legacySelector).forEach((element) => element.classList.add('scroll-trigger--design-mode'));
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    elementsIn(event.target, legacySelector).forEach((element) => element.classList.remove('scroll-trigger--design-mode'));
  });

  const handleReducedMotionChange = () => {
    observer?.disconnect();
    observer = null;
    heroTrustObserver?.disconnect();
    heroTrustObserver = null;
    sectionObserver?.disconnect();
    sectionObserver = null;
    sectionGlowObserver?.disconnect();
    sectionGlowObserver = null;
    secondaryObserver?.disconnect();
    secondaryObserver = null;
    if (reducedMotion.matches) {
      rootElement.classList.remove('sg-motion-ready');
      showFinal(document);
    } else {
      initializeSafely(document);
      elementsIn(document, '[data-sg-hero]').forEach((hero) => {
        const state = heroStates.get(hero);
        observeHeroTrustGlow(hero, state?.trust);
      });
    }
  };
  if (reducedMotion.addEventListener) {
    reducedMotion.addEventListener('change', handleReducedMotionChange);
  } else {
    reducedMotion.addListener(handleReducedMotionChange);
  }

  const handleDesktopDepthChange = () => {
    if (!desktopDepth.matches) resetDepth(document, true);
    prepareDepthIn(document);
  };
  if (desktopDepth.addEventListener) {
    desktopDepth.addEventListener('change', handleDesktopDepthChange);
  } else {
    desktopDepth.addListener(handleDesktopDepthChange);
  }

  window.addEventListener('pageshow', (event) => {
    // A bfcache restore may retain intermediate classes or count frames; always resume from a usable final state.
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (event.persisted || navigation?.type === 'back_forward') {
      showFinal(document);
      prepareDepthIn(document);
      return;
    }
    initializeSafely(document);
  });

  window.SignGuyMotion = { filterGallery, initialize: initializeSafely, showFinal };
  initializeSafely(document);
})();
